import { Component, ChangeDetectionStrategy, input, signal, computed, resource, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company, UpdateCompanynameRequest } from '../customer.model';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { CustomerService } from '../customer.service';
import { ErrorService } from '../../../common/errors/error.service';
import { environment } from 'src/environments/environment';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-customer-company-table',
    templateUrl: './customer-company-table.component.html',
    styleUrl: './customer-company-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgbTooltip]
})
export class CustomerCompanyTableComponent {
  private readonly toastrService = inject(ToastrService);
  private readonly customerService = inject(CustomerService);
  private readonly commonService = inject(CommonService);
  private readonly modalService = inject(ModalService);
  private readonly errorService = inject(ErrorService);

  readonly customerId = input.required<number>();
  readonly searchText = input<string>('');


  protected readonly companiesResource = resource({
    params: () => this.customerId(),
    loader: async ({ params }) => {
      const companies = await this.customerService.getCompanies(params);
      return companies.map(c => ({
        ...c,
        isChanged: false,
        isEditing: false,
        isSaving: false,
        isUrlChanged: false,
        tempName: c.name,
        tempNameUrl: this.safeDecode(c.nameUrl)
      }));
    }
  });

  readonly filterCompanies = computed(() => {
    if (!this.companiesResource.hasValue()) return [];
    const search = this.searchText().trim().toLowerCase();
    const companies = this.companiesResource.value();
    if (!search) return companies;
    return companies.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.nameUrl.toLowerCase().includes(search) ||
      c.companyId.toString().startsWith(search)
    );
  });

  readonly isEditing = signal(false);
  readonly environment = environment.environment;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.commonService.CompanyListChanged.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      this.companiesResource.reload();
    });
    this.commonService.ImportCompanies.pipe(takeUntilDestroyed(destroyRef)).subscribe((response: string) => {
      if (response === 'refresh') {
        this.companiesResource.reload();
      }
    });
  }

  setCompanyNameEditable(company: Company, row: HTMLTableRowElement): void {
    if (!this.companiesResource.hasValue()) return;
    this.companiesResource.set(
      this.companiesResource.value().map(c => c.companyId === company.companyId ? { ...c, isEditing: true } : c)
    );
    this.isEditing.set(true);
    setTimeout(() => {
      const input = row.querySelector('input');
      if (input) input.focus();
    });
  }

  onResetCompanyItem(company: Company): void {
    if (!this.companiesResource.hasValue()) return;
    this.companiesResource.set(
      this.companiesResource.value().map(c => c.companyId === company.companyId
        ? { ...c, isChanged: false, isUrlChanged: false, isEditing: false, tempName: c.name, tempNameUrl: this.safeDecode(c.nameUrl) }
        : c)
    );
    this.isEditing.set(false);
  }

  onEditCompanyName($event: any, company: Company): void {
    if (!this.companiesResource.hasValue()) return;
    const tempName = $event.target.value;
    // Display name (Alias) is decoupled from the URL (Navn) — editing it no longer rewrites the URL (issue #95).
    this.companiesResource.set(
      this.companiesResource.value().map(c => c.companyId === company.companyId
        ? { ...c, isChanged: true, tempName }
        : c)
    );
  }

  onEditCompanyUrl($event: any, company: Company): void {
    if (!this.companiesResource.hasValue()) return;
    const tempNameUrl = $event.target.value;
    this.companiesResource.set(
      this.companiesResource.value().map(c => c.companyId === company.companyId
        ? { ...c, isChanged: true, isUrlChanged: true, tempNameUrl }
        : c)
    );
  }

  onSaveCompanyItem(company: Company): void {
    if (!this.companiesResource.hasValue()) return;
    this.companiesResource.set(
      this.companiesResource.value().map(c => c.companyId === company.companyId ? { ...c, isSaving: true } : c)
    );
    // Only re-encode and send a new URL when the admin actually edited it; otherwise pass the stored
    // value back untouched so existing Navn values are preserved byte-for-byte (issue #95).
    const newNameUrl = company.isUrlChanged ? encodeURIComponent(company.tempNameUrl) : company.nameUrl;
    const request: UpdateCompanynameRequest = {
      customerId: this.customerId(),
      companyId: company.companyId,
      name: company.tempName,
      nameUrl: newNameUrl
    };
    this.customerService.updateCompanyname(request)
      .then(() => {
        if (!this.companiesResource.hasValue()) return;
        this.companiesResource.set(
          this.companiesResource.value().map(c => c.companyId === company.companyId
            ? { ...c, name: company.tempName, nameUrl: newNameUrl, tempNameUrl: this.safeDecode(newNameUrl), isEditing: false, isChanged: false, isUrlChanged: false, isSaving: false }
            : c)
        );
        this.isEditing.set(false);
        this.toastrService.success('Regnskabsnavn for [' + company.companyId + '] er opdateret');
      })
      .catch((error: any) => {
        if (!this.companiesResource.hasValue()) return;
        this.companiesResource.set(
          this.companiesResource.value().map(c => c.companyId === company.companyId ? { ...c, isSaving: false } : c)
        );
      });
  }

  createSync(companyId: number): void {
    this.modalService.showCreateSyncronizationContract(this.customerId(), companyId);
  }

  onDeleteCompanyItem(company: Company): void {
    this.commonService.confirm('Slet regnskab: ' + company.name, 'Er du sikker på at du vil slette regnskabet?', 'Ja', 'Nej', 'lg')
      .then((confirmed) => {
        if (confirmed) {
          this.customerService.deleteCompany(this.customerId(), company.companyId)
            .then(() => {
              this.toastrService.success('Regnskab [' + company.companyId + '] er slettet');
              this.companiesResource.reload();
            })
            .catch((response: any) => {
              // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
            });
        }
      })
      .catch(() => { });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastrService.success(`${text} kopieret til udklipsholder`);
    }, (err) => {
      this.errorService.warning('Fejl ved kopiering til udklipsholder: ' + err);
    });
  }

  /** Preview of what gets stored in Navn — the user types plain text, we URI-encode behind the scenes. */
  encodeUrl(value: string): string {
    return encodeURIComponent(value ?? '');
  }

  /** Decode a stored (encoded) Navn back to plain text for editing; fall back to the raw value on malformed legacy data. */
  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
